// material-ui
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Modal,
    Button,
    Typography,
    TextField,
    FormControlLabel,
    Checkbox,
    Stack,
    Chip,
    InputAdornment,
    CircularProgress,
    Avatar,
    Autocomplete
} from '@mui/material';
import { 
    Add, 
    CloudUpload, 
    Delete, 
    ContentCopy, 
    CheckCircle, 
    Cancel as CancelIcon,
    TrackChanges 
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { red, green, blue, grey, orange } from '@mui/material/colors';
import { axiosInstance } from 'utils/axios';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from 'features/auth/authSlice';
import { successAlert, errorAlert } from 'ui-component/Toasts';
import { useTranslation } from "react-i18next";
import axios from 'axios';

// Modal Style
const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 450,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2
};

const StocksTab = () => {
    const { t } = useTranslation();
    const token = useSelector(selectCurrentToken);

    // Data State
    const [programDetails, setProgramDetails] = useState([]);
    const [updateButtonDisabled, setUpdateButtonDisabled] = useState(false);

    // Submission Loading States
    const [isSubmittingClone, setIsSubmittingClone] = useState(false);
    const [isSubmittingTarget, setIsSubmittingTarget] = useState(false);

    // Insertion Modal State
    const [open, setOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [newActive, setNewActive] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);

    // Clone Modal State (Binance)
    const [cloneModalOpen, setCloneModalOpen] = useState(false);
    const [selectedCrypto, setSelectedCrypto] = useState(null);
    const [binanceSymbol, setBinanceSymbol] = useState('');
    const [isBinanceValid, setIsBinanceValid] = useState(null); 
    const [binancePrice, setBinancePrice] = useState('');
    const [isValidating, setIsValidating] = useState(false);

    // Target Price Modal State
    const [targetPriceModalOpen, setTargetPriceModalOpen] = useState(false);
    const [targetPrice, setTargetPrice] = useState('');
    const [estimatedMinutes, setEstimatedMinutes] = useState('');

    // --- ADD THIS BLOCK FOR IPO ---
    const [ipoModalOpen, setIpoModalOpen] = useState(false);
    const [ipoLaunchDate, setIpoLaunchDate] = useState('');
    const [ipoMinAmount, setIpoMinAmount] = useState('');
    const [ipoStartingPrice, setIpoStartingPrice] = useState('');
    const [ipoTradeStartDate, setIpoTradeStartDate] = useState('');
    const [ipoLinkedCryptoSymbol, setIpoLinkedCryptoSymbol] = useState(null); // <--- ADD THIS
    const [isSubmittingIPO, setIsSubmittingIPO] = useState(false);
    // ------------------------------
    // --- ADD THIS BLOCK FOR DELETE ---
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [cryptoToDelete, setCryptoToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    // ---------------------------------

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setNewName('');
        setNewActive(true);
        setSelectedImage(null);
    };

    const handleCloseClone = () => {
        setCloneModalOpen(false);
        setBinanceSymbol('');
        setBinancePrice('');
        setIsBinanceValid(null);
        setSelectedCrypto(null);
        setIsSubmittingClone(false);
    };

    const handleCloseTargetPrice = () => {
        setTargetPriceModalOpen(false);
        setTargetPrice('');
        setEstimatedMinutes('');
        setSelectedCrypto(null);
        setIsSubmittingTarget(false);
    };

    // --- ADD THIS BLOCK FOR IPO ---
    const handleCloseIPO = () => {
        setIpoModalOpen(false);
        setIpoLaunchDate('');
        setIpoMinAmount('');
        setIpoStartingPrice('');
        setIpoTradeStartDate('');
        setIpoLinkedCryptoSymbol(null); // <--- ADD THIS
        setSelectedCrypto(null);
        setIsSubmittingIPO(false);
    };

    const handleIPOSubmit = async () => {
        setIsSubmittingIPO(true);

        // Helper to keep exact local time but format it with 'Z' for the backend
        const toLocalISOString = (dateString) => {
            if (!dateString) return null;
            const date = new Date(dateString);
            return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
        };

        try {
            const res = await axiosInstance.put('/CryptoSymbol/IPODetails', {
                id: selectedCrypto.id,
                launchDate: toLocalISOString(ipoLaunchDate),
                minAmountBeforeLaunchDate: parseFloat(ipoMinAmount),
                startingPrice: parseFloat(ipoStartingPrice),
                tradeStartDate: toLocalISOString(ipoTradeStartDate),
                linkedCryptoSymbolID: ipoLinkedCryptoSymbol ? ipoLinkedCryptoSymbol.id : 0 // <--- ADD THIS
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.status === 200) {
                successAlert('IPO details updated successfully');
                handleCloseIPO();
                fetchData(); 
            }
        } catch (error) {
            errorAlert('Failed to set IPO details');
        } finally {
            setIsSubmittingIPO(false);
        }
    };

    // --- ADD THIS BLOCK FOR DELETE ---
    const handleCloseDelete = () => {
        setDeleteModalOpen(false);
        setCryptoToDelete(null);
        setIsDeleting(false);
    };

    const handleDeleteSubmit = async () => {
        if (!cryptoToDelete) return;
        setIsDeleting(true);
        try {
            const res = await axiosInstance.post(`/CryptoSymbol/Delete/${cryptoToDelete.id}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.status === 200) {
                successAlert('Successfully deleted');
                handleCloseDelete();
                fetchData(); // Refresh the table
            }
        } catch (error) {
            errorAlert('Failed to delete crypto');
        } finally {
            setIsDeleting(false);
        }
    };
    // ---------------------------------

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedImage(e.target.files[0]);
        }
    };

    const fetchData = async () => {
        try {
            const res = await axiosInstance.get(`/CryptoSymbols/All`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (res.status === 200) {
                // Filter the array to only include items where cryptoSymbolTypeID is 1
                const filteredData = res.data.filter(item => item.cryptoSymbolTypeID === 2);
                
                setProgramDetails(filteredData);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const submitUpdateProgram = async (id, active) => {
        setUpdateButtonDisabled(true);
        try {
            const res = await axiosInstance.put(
                '/CryptoSymbol/Active',
                { id: id, active: active },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.status === 200) {
                successAlert('Successfully Updated');
                fetchData();
            }
        } catch (error) {
            errorAlert('Error occurred');
        } finally {
            setUpdateButtonDisabled(false);
        }
    };

    const handleCreateCrypto = async () => {
        if (!newName.trim()) {
            errorAlert("Name is required");
            return;
        }
        setUpdateButtonDisabled(true);
        try {
            const res = await axiosInstance.post(
                '/CryptoSymbol',
                { code: newName, name: newName, active: newActive, isMemeCoin: true, cryptoSymbolTypeID: 2 },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (res.status === 200 || res.status === 201) {
                const newCryptoId = res.data;
                if (selectedImage && newCryptoId) {
                    const formData = new FormData();
                    formData.append('images', selectedImage);
                    await axiosInstance.post(`/CryptoSymbol/Image/Multiple?cryptoSymbolID=${newCryptoId}`, formData, {
                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                    });
                }
                successAlert('Successfully Added');
                handleClose();
                fetchData();
            }
        } catch (error) {
            errorAlert('Failed to add crypto');
        } finally {
            setUpdateButtonDisabled(false);
        }
    };

    // Reverted to Binance API
    const validateBinanceSymbol = async (symbol) => {
        if (!symbol) {
            setIsBinanceValid(null);
            setBinancePrice('');
            return;
        }
        setIsValidating(true);
        try {
            const response = await axios.get(`https://api.binance.com/api/v3/ticker/price?symbol=${symbol.toUpperCase()}`);
            if (response.status === 200) {
                setIsBinanceValid(true);
                setBinancePrice(response.data.price);
            }
        } catch (error) {
            setIsBinanceValid(false);
            setBinancePrice('');
        } finally {
            setIsValidating(false);
        }
    };

    const handleCloneSubmit = async () => {
        setIsSubmittingClone(true);
        try {
            const res = await axiosInstance.post('/Candlesticks/Clone', {
                mySymbolID: selectedCrypto.id,
                binanceSymbol: binanceSymbol.toUpperCase() 
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.status === 200) {
                successAlert('Clone started successfully');
                handleCloseClone();
            }
        } catch (error) {
            errorAlert('Clone failed');
        } finally {
            setIsSubmittingClone(false);
        }
    };

    const handleTargetPriceSubmit = async () => {
        setIsSubmittingTarget(true);
        try {
            const res = await axiosInstance.put('/PriceState/TargetPrice', {
                cryptoSymbolID: selectedCrypto.id,
                targetPrice: parseFloat(targetPrice),
                estimatedMinutes: parseInt(estimatedMinutes)
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.status === 200) {
                successAlert('Target price set successfully');
                handleCloseTargetPrice();
            }
        } catch (error) {
            errorAlert('Failed to set target price');
        } finally {
            setIsSubmittingTarget(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button variant="contained" startIcon={<Add />} onClick={handleOpen} color="primary">
                    {t("masterData.insertUSStocks") || 'Add US Stocks'}
                </Button>
            </Box>

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ height: '75vh' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("masterData.crypto")}</TableCell>
                                <TableCell>Type</TableCell>
                                
                                {/* --- ADD THIS ROW --- */}
                                <TableCell>IPO Details</TableCell>
                                {/* -------------------- */}

                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {programDetails.map((row) => (
                                <TableRow hover key={row.id}>
                                    <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Avatar 
                                                src={row.logoUrl} 
                                                variant="rounded" 
                                                sx={{ width: 30, height: 30, bgcolor: grey[200] }}
                                            >
                                                {row.code?.charAt(0)}
                                            </Avatar>
                                            <Typography variant="subtitle2">
                                                {row.code} {' '} {row.active === true ? <span style={{color: 'green'}}>●</span> : <span style={{color: 'red'}}>●</span>}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        {row.isMemeCoin ? 
                                            <Chip label={t("masterData.manual")} size="small" sx={{ bgcolor: blue[100], color: blue[800] }} /> : 
                                            <Chip label={t("masterData.onChain")} size="small" sx={{ bgcolor: grey[200] }} />
                                        }
                                    </TableCell>

                                    {/* --- IPO DETAILS TABLE CELL --- */}
                                    <TableCell>
                                        {row.isMemeCoin && row.launchDate ? (
                                            <Box sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                                                <div><b>Launch Date:</b> {row.launchDate.replace('T', ' ').substring(0, 16)}</div>
                                                <div><b>Min Amount:</b> {row.minAmountBeforeLaunchDate}</div>
                                                <div><b>Start Price:</b> ${row.startingPrice}</div>
                                                <div><b>Trade Start:</b> {row.tradeStartDate ? row.tradeStartDate.replace('T', ' ').substring(0, 16) : '-'}</div>
                                                
                                                {/* --- ADD THIS NEW LINE FOR LINKED COIN --- */}
                                                <div>
                                                    <b>Able to Purchase By Using:</b> {
                                                        row.linkedCryptoSymbolID 
                                                            ? programDetails.find(p => p.id === row.linkedCryptoSymbolID)?.code || '-' 
                                                            : '-'
                                                    }
                                                </div>
                                                {/* ----------------------------------------- */}
                                                
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" color="textSecondary">-</Typography>
                                        )}
                                    </TableCell>
                                    {/* ----------------------------------------------- */}
                                    <TableCell align="center">
                                        <Stack direction="row" spacing={1} justifyContent="center">
                                            <Button
                                                variant="contained"
                                                size="small"
                                                sx={{
                                                    width: '100px',
                                                    backgroundColor: row.active ? red[500] : green[500],
                                                    '&:hover': { backgroundColor: row.active ? red[700] : green[700] },
                                                }}
                                                onClick={() => submitUpdateProgram(row.id, !row.active)}
                                                disabled={updateButtonDisabled}
                                            >
                                                {row.active ? "Stop" : "Start"}
                                            </Button>
                                            
                                            {row.isMemeCoin && (
                                                <>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        color="secondary"
                                                        startIcon={<ContentCopy />}
                                                        onClick={() => {
                                                            setSelectedCrypto(row);
                                                            setCloneModalOpen(true);
                                                        }}
                                                    >
                                                        Clone
                                                    </Button>

                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{ color: orange[800], borderColor: orange[800] }}
                                                        startIcon={<TrackChanges />}
                                                        onClick={() => {
                                                            setSelectedCrypto(row);
                                                            setTargetPriceModalOpen(true);
                                                        }}
                                                    >
                                                        Target
                                                    </Button>
                                                    
                                                    {/* --- REPLACE YOUR EXISTING IPO BUTTON WITH THIS --- */}
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            sx={{ color: blue[800], borderColor: blue[800] }}
                                                            startIcon={<TrackChanges />}
                                                            onClick={() => {
                                                                setSelectedCrypto(row);
                                                                // Pre-fill existing data if it exists
                                                                setIpoLaunchDate(row.launchDate ? row.launchDate.slice(0, 16) : '');
                                                                setIpoMinAmount(row.minAmountBeforeLaunchDate || '');
                                                                setIpoStartingPrice(row.startingPrice || '');
                                                                setIpoTradeStartDate(row.tradeStartDate ? row.tradeStartDate.slice(0, 16) : '');
                                                                // <--- ADD THIS BLOCK --->
                                                                setIpoLinkedCryptoSymbol(
                                                                    row.linkedCryptoSymbolID 
                                                                        ? programDetails.find(p => p.id === row.linkedCryptoSymbolID) || null 
                                                                        : null
                                                                );
                                                                // <----------------------->
                                                                setIpoModalOpen(true);
                                                            }}
                                                        >
                                                            IPO
                                                        </Button>
                                                    {/* ------------------------------------------------ */}
                                                </>
                                            )}

                                            {/* --- ADD DELETE BUTTON HERE --- */}
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                sx={{ color: red[500], borderColor: red[500] }}
                                                startIcon={<Delete />}
                                                onClick={() => {
                                                    setCryptoToDelete(row);
                                                    setDeleteModalOpen(true);
                                                }}
                                            >
                                                Delete
                                            </Button>
                                            {/* ------------------------------ */}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Insertion Modal */}
            <Modal open={open} onClose={handleClose}>
                <Box sx={modalStyle}>
                    <Typography variant="h4" sx={{ mb: 3 }}>{t("masterData.insertUSStocks")}</Typography>
                    <Stack spacing={3}>
                        <TextField
                            fullWidth
                            label={t("trade.usStocksName")}
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                        />
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t("trade.usStocksIcon")}</Typography>
                            <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />} color={selectedImage ? "success" : "primary"}>
                                {selectedImage ? t("trade.changePhoto") : t("trade.selectPhoto")}
                                <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                            </Button>
                            {selectedImage && <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>{selectedImage.name}</Typography>}
                        </Box>
                        <FormControlLabel
                            control={<Checkbox checked={newActive} onChange={(e) => setNewActive(e.target.checked)} />}
                            label={t("trade.active")}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button onClick={handleClose} variant="outlined">{t("trade.cancel")}</Button>
                            <Button onClick={handleCreateCrypto} variant="contained" disabled={updateButtonDisabled || !newName}>{t("trade.submit")}</Button>
                        </Box>
                    </Stack>
                </Box>
            </Modal>

            {/* Clone Modal (Binance) */}
            <Modal open={cloneModalOpen} onClose={handleCloseClone}>
                <Box sx={modalStyle}>
                    <Typography variant="h4" sx={{ mb: 1 }}>Clone from Binance</Typography>
                    <Typography variant="body2" sx={{ mb: 3 }}>Cloning for: <b>{selectedCrypto?.code}</b></Typography>
                    <Stack spacing={3}>
                        <TextField
                            fullWidth
                            label="Binance Symbol (e.g. BTCUSDT)"
                            placeholder="Enter Binance Symbol"
                            value={binanceSymbol}
                            onChange={(e) => {
                                setBinanceSymbol(e.target.value);
                                setIsBinanceValid(null);
                            }}
                            onBlur={(e) => validateBinanceSymbol(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {isValidating && <CircularProgress size={20} />}
                                        {!isValidating && isBinanceValid === true && <CheckCircle sx={{ color: green[500] }} />}
                                        {!isValidating && isBinanceValid === false && <CancelIcon sx={{ color: red[500] }} />}
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {binancePrice && (
                            <Typography variant="h5" color="primary" align="center">
                                Binance Latest Price: ${binancePrice}
                            </Typography>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button onClick={handleCloseClone} variant="outlined" disabled={isSubmittingClone}>Cancel</Button>
                            <Button 
                                onClick={handleCloneSubmit} 
                                variant="contained" 
                                disabled={isSubmittingClone || !isBinanceValid}
                                startIcon={isSubmittingClone ? <CircularProgress size={20} color="inherit" /> : null}
                            >
                                {isSubmittingClone ? "Processing..." : "Start Clone"}
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Modal>

            {/* Target Price Modal */}
            <Modal open={targetPriceModalOpen} onClose={handleCloseTargetPrice}>
                <Box sx={modalStyle}>
                    <Typography variant="h4" sx={{ mb: 1 }}>Set Target Price</Typography>
                    <Typography variant="body2" sx={{ mb: 3 }}>Setting price for: <b>{selectedCrypto?.code}</b></Typography>
                    <Stack spacing={3}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Target Price"
                            placeholder="0.00"
                            value={targetPrice}
                            onChange={(e) => setTargetPrice(e.target.value)}
                        />
                        <TextField
                            fullWidth
                            type="number"
                            label="Estimated Minutes to Reach"
                            placeholder="e.g. 60"
                            value={estimatedMinutes}
                            onChange={(e) => setEstimatedMinutes(e.target.value)}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button onClick={handleCloseTargetPrice} variant="outlined" disabled={isSubmittingTarget}>Cancel</Button>
                            <Button 
                                onClick={handleTargetPriceSubmit} 
                                variant="contained" 
                                color="primary"
                                disabled={isSubmittingTarget || !targetPrice || !estimatedMinutes}
                                startIcon={isSubmittingTarget ? <CircularProgress size={20} color="inherit" /> : null}
                            >
                                {isSubmittingTarget ? "Updating..." : "Update Price"}
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Modal>

            {/* --- ADD THIS NEW MODAL FOR IPO --- */}
            <Modal open={ipoModalOpen} onClose={handleCloseIPO}>
                <Box sx={modalStyle}>
                    <Typography variant="h4" sx={{ mb: 1 }}>Set IPO Details</Typography>
                    <Typography variant="body2" sx={{ mb: 3 }}>Setting IPO for: <b>{selectedCrypto?.code}</b></Typography>
                    <Stack spacing={3}>
                        <TextField
                            fullWidth
                            type="datetime-local"
                            label="Launch Date"
                            InputLabelProps={{ shrink: true }}
                            value={ipoLaunchDate}
                            onChange={(e) => setIpoLaunchDate(e.target.value)}
                        />
                        <TextField
                            fullWidth
                            type="number"
                            label="Min Amount Before Launch"
                            placeholder="0"
                            value={ipoMinAmount}
                            onChange={(e) => setIpoMinAmount(e.target.value)}
                        />
                        <TextField
                            fullWidth
                            type="number"
                            label="Starting Price"
                            placeholder="0.00"
                            value={ipoStartingPrice}
                            onChange={(e) => setIpoStartingPrice(e.target.value)}
                        />
                        <TextField
                            fullWidth
                            type="datetime-local"
                            label="Trade Start Date"
                            InputLabelProps={{ shrink: true }}
                            value={ipoTradeStartDate}
                            onChange={(e) => setIpoTradeStartDate(e.target.value)}
                        />
                        
                        {/* <--- ADD THIS AUTOCOMPLETE ---> */}
                        <Autocomplete
                            options={programDetails}
                            getOptionLabel={(option) => option.code || ''}
                            value={ipoLinkedCryptoSymbol}
                            onChange={(event, newValue) => {
                                setIpoLinkedCryptoSymbol(newValue);
                            }}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => (
                                <TextField 
                                    {...params} 
                                    label="Linked Crypto Symbol (Optional)" 
                                    placeholder="Select a symbol" 
                                />
                            )}
                        />
                        {/* <----------------------------> */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button onClick={handleCloseIPO} variant="outlined" disabled={isSubmittingIPO}>Cancel</Button>
                            <Button 
                                onClick={handleIPOSubmit} 
                                variant="contained" 
                                color="primary"
                                disabled={isSubmittingIPO || !ipoLaunchDate || !ipoMinAmount || !ipoStartingPrice || !ipoTradeStartDate}
                                startIcon={isSubmittingIPO ? <CircularProgress size={20} color="inherit" /> : null}
                            >
                                {isSubmittingIPO ? "Updating..." : "Update IPO"}
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Modal>
            {/* ---------------------------------- */}

             {/* --- ADD THIS NEW MODAL FOR DELETE --- */}
            <Modal open={deleteModalOpen} onClose={handleCloseDelete}>
                <Box sx={modalStyle}>
                    <Typography variant="h4" sx={{ mb: 2 }}>Confirm Delete</Typography>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                        Are you sure you want to delete <b>{cryptoToDelete?.code}</b>? This action cannot be undone.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseDelete} variant="outlined" disabled={isDeleting}>Cancel</Button>
                        <Button 
                            onClick={handleDeleteSubmit} 
                            variant="contained" 
                            color="error"
                            disabled={isDeleting}
                            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            {isDeleting ? "Deleting..." : "Delete"}
                        </Button>
                    </Box>
                </Box>
            </Modal>
            {/* ------------------------------------- */}
        </>
    );
};

export default StocksTab;